import * as React from 'react';
import {
    Edit,
    TextInput,
    useTranslate,
    useRecordContext,
    required,
    SelectInput,
    TabbedForm,
    useNotify,
    useGetList,
    useRedirect,
    regex,
} from 'react-admin';
import { Users } from '../../custom';
import { BASE_URL, BASE_URL_GO, COUNTRY_CODES, USER_STATUSES } from '../../common/constants';
import FileUpload from '../../common/inputs/FileUpload';
import {
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import HTMLEditorInput, { validHtml } from '../../common/inputs/HTMLEditorInput';
import { EditToolbar } from '../../common/EditToolbar';
import { Public } from '@mui/icons-material';
import Button from '@mui/material/Button';
import { useState } from 'react';
import useIsPermission from '../../hooks/useIsPermission';
import PermissionGuard from '../../common/PermissionGuard';
import { redirect, useParams } from 'react-router';
import {
    validateEmail,
    validateNotEmpty,
    validatePhoneNumber,
} from '../../common/VideoPlayer/util';
import { goApi } from '../../services';

const validateNameNoNumbers = regex(/^[A-Za-z\s]+$/, 'Name cannot contain numbers.');
export enum ClientType {
    Chrome = 'Chrome',
    Firefox = 'Firefox',
    Safari = 'Safari',
    Edge = 'Edge',
    InternetExplorer = 'Internet Explorer',
    Opera = 'Opera',
    Other = 'Other',
}

function detectClientType(userAgent: string): ClientType {
    if (userAgent.includes('Chrome')) return ClientType.Chrome;
    if (userAgent.includes('Firefox')) return ClientType.Firefox;
    if (userAgent.includes('Safari')) return ClientType.Safari;
    if (userAgent.includes('Edge')) return ClientType.Edge;
    if (userAgent.includes('MSIE') || userAgent.includes('Trident/'))
        return ClientType.InternetExplorer;
    if (userAgent.includes('Opera')) return ClientType.Opera;
    return ClientType.Other;
}

interface ClientDetectorProps {
    userAgent: string;
}

const ClientTypeField = () => {
    const record = useRecordContext();
    const clientType = detectClientType(record?.deviceDetails);
    return (
        <div className={'flex gap-4'}>
            {/*<div className={'px-2 py-0.5 bg-red-500 text-white rounded-md'}>{clientType}</div>*/}
            <div>{record.platform === 'WEB' && <Public className={'text-blue-500'} />}</div>
        </div>
    );
};

ClientTypeField.defaultProps = {
    label: 'Device Type',
};

function generateRandomPassword(length: number) {
    const charset = '0123456789';
    let password = '';
    for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * charset.length);
        password += charset[randomIndex];
    }
    return password;
}

const UserEditForm = () => {
    const [updatedPassword, setUpdatedPassword] = useState('');
    const [updatingPassword, setUpdatingPassword] = useState(false);
    const record = useRecordContext();
    const [role, setRole] = React.useState(record?.role);
    const [roleId, setRoleId] = React.useState(record?.roleId);
    const notify = useNotify();
    const { id } = useParams();
    const redirect = useRedirect();
    const { data: rolesList = [] } = useGetList<any>('roles', {
        sort: { field: 'createdAt', order: 'ASC' },
        pagination: { page: 1, perPage: 100 },
    });

    const predefinedRoles = [{ name: 'STUDENT', id: '4d8c2d80-a3e9-4d1a-be12-08def4829437' }];

    const roleOptions = [
        ...predefinedRoles,
        ...rolesList.map(role => ({
            name: role.name,
            id: role.id,
        })),
    ];

    const handleSubmit = async (values: any) => {
        try {
            const selectedRoleOption = roleOptions.find(option => option.id === roleId);
            const finalData = {
                ...values,
                role: selectedRoleOption?.name || '',
                roleId: roleId,
            };

            const res = await goApi.put(`/users/${id}`, finalData);

            notify('User updated successfully', { type: 'success' });
            redirect('/users');
        } catch (error: any) {
            console.error('Error updating user:', error);
            notify(error?.message || 'Failed to update user', { type: 'warning' });
        }
    };
    
    return (
        <TabbedForm onSubmit={handleSubmit} toolbar={<EditToolbar />}>
            <TabbedForm.Tab label="resources.users.tabs.details">
                {/* <TextInput disabled source="role" fullWidth /> */}
                <FormControl fullWidth required>
                    <InputLabel>Role</InputLabel>
                    <Select
                        value={roleId}
                        onChange={e => {
                            const selectedRole = roleOptions.find(
                                option => option.id === e.target.value,
                            );
                            setRoleId(e.target.value);
                            setRole(selectedRole?.name || '');
                        }}
                        fullWidth
                        disabled
                    >
                        {roleOptions.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FileUpload
                    type="VIDEO_THUMBNAIL"
                    source="profileImage"
                    label={'Profile Image'}
                    accept={'image/*'}
                />
                <TextInput
                    source="firstName"
                    validate={[required(), validateNotEmpty, validateNameNoNumbers]}
                    fullWidth
                />
                <TextInput
                    source="lastName"
                    validate={[required(), validateNotEmpty, validateNameNoNumbers]}
                    fullWidth
                />
                <SelectInput source="countryCode" choices={COUNTRY_CODES} fullWidth />
                <TextInput source="phone" validate={[required(), validatePhoneNumber]} fullWidth />
                <TextInput source="email" validate={[required(), validateEmail]} fullWidth />
                <TextInput source="admissionNumber" fullWidth />
                <SelectInput source="status" choices={USER_STATUSES} fullWidth />
                <Divider />
                <div className={'text-xl font-bold mb-2'}>Change Password</div>
                <div className={'flex space-x-2 items-center'}>
                    {updatedPassword && <div>{updatedPassword}</div>}
                    {updatingPassword ? (
                        <CircularProgress />
                    ) : (
                        <Button
                            onClick={async () => {
                                try {
                                    setUpdatingPassword(true);

                                    const newP = generateRandomPassword(6);

                                    const res: any = await goApi.put(
                                        `/users/${record.id}/set-user-password`,
                                        {
                                            userId: record.id,
                                            password: newP,
                                        }
                                    );

                                    if (!res?.error) {
                                        setUpdatedPassword(newP);
                                    }
                                } catch (error: any) {
                                    console.error("Failed to update password:", error);
                                    notify(error?.message || "Failed to update password", { type: "warning" });
                                } finally {
                                    setUpdatingPassword(false);
                                }
                            }}
                        >
                            Update Password
                        </Button>
                    )}
                </div>
            </TabbedForm.Tab>

            <TabbedForm.Tab
                label="resources.users.tabs.addresses"
                path="addresses"
                sx={{ maxWidth: '40em' }}
            >
                <HTMLEditorInput source="description" label="" validate={[validHtml]} />
            </TabbedForm.Tab>
        </TabbedForm>
    );
};

const UserEdit = () => {
    const { isUpdatable } = useIsPermission('USERS');
    return (
        <PermissionGuard permission={isUpdatable}>
            <Edit title={<UserTile />}>
                <UserEditForm />
            </Edit>
        </PermissionGuard>
    );
};

const UserTile = () => {
    const record = useRecordContext<Users>();
    const translate = useTranslate();

    return record ? (
        <span>
            {translate('resources.users.name', { smart_count: 1 })} &quot;
            {record.first_name}&quot;
            {record.last_name}&quot;
        </span>
    ) : null;
};

export default UserEdit;
